import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { callbackGuard } from './core/auth/callback.guard';
import { agentsNavEnabledGuard } from './core/feature-flags/agents-nav-enabled.guard';
import { tenantMembershipsAdminGuard } from './core/auth/tenant-memberships-admin.guard';
import { uiConfigAdminGuard } from './core/auth/ui-config-admin.guard';
import { configurationNavEnabledGuard } from './core/feature-flags/configuration-nav-enabled.guard';
import { pricingEnabledGuard } from './core/feature-flags/pricing-enabled.guard';
import { scorecardEnabledGuard } from './core/feature-flags/scorecard-enabled.guard';
import {
  CmiDrilldownConfig,
  CmiViewAllConfig,
  deriveCmiAggregateViews,
} from './shared/models/cmi-insight-routes.config';

export const routes: Routes = [
  {
    path: 'login/complete',
    canActivate: [callbackGuard],
    loadComponent: () => import('./core/auth/callback/login.complete').then((m) => m.LoginComplete),
  },
  {
    path: 'login',
    loadComponent: () => import('./core/auth/pages/login/login').then((m) => m.Login),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/app-layout').then((m) => m.AppLayout),
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'home',
        loadComponent: () =>
          import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./pages/home/home').then((m) => m.Home),
          },
          {
            path: 'insights',
            loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
            data: {
              cmiViewAllConfig: {
                investigationType: '',
                title: 'All Insights',
                backRoute: '/home',
                parentRoute: '/home',
                drilldownRoute: '/cmi/insights',
                entityParamKey: 'entityId',
                drilldownParamField: 'id',
              } satisfies CmiViewAllConfig,
            },
          },
        ],
      },
      {
        path: 'sales-rep-performance',
        loadComponent: () =>
          import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/sales-rep-performance/sales-rep-performance').then(
                (m) => m.SalesRepPerformance
              ),
          },
          {
            path: 'coaching-opportunities',
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: {
                    investigationType: 'coaching_opportunities',
                    title: 'Coaching Opportunities',
                    backRoute: '/sales-rep-performance',
                    parentRoute: '/sales-rep-performance',
                    drilldownRoute: '/sales-rep-performance/coaching-opportunities',
                    entityParamKey: 'repName',
                  } satisfies CmiViewAllConfig,
                },
              },
              {
                path: ':repName',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'coaching_opportunities',
                    entityParamKey: 'repName',
                    backRoute: '/sales-rep-performance',
                    title: 'Coaching Opportunities',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'customer-performance',
        loadComponent: () =>
          import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/customer-performance/customer-performance').then(
                (m) => m.CustomerPerformance
              ),
          },
          {
            path: 'win-back-opportunities',
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: {
                    investigationType: 'customer_win_back',
                    title: 'Win Back Opportunities',
                    backRoute: '/customer-performance',
                    parentRoute: '/customer-performance',
                    drilldownRoute: '/customer-performance/win-back-opportunities',
                    entityParamKey: 'customerName',
                  } satisfies CmiViewAllConfig,
                },
              },
              {
                path: ':customerName',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'customer_win_back',
                    entityParamKey: 'customerName',
                    backRoute: '/customer-performance',
                    title: 'Win Back Opportunities',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
          {
            path: 'customers-at-risk',
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: {
                    investigationType: 'customer_at_risk',
                    title: 'Customers At Risk',
                    backRoute: '/customer-performance',
                    parentRoute: '/customer-performance',
                    drilldownRoute: '/customer-performance/customers-at-risk',
                    entityParamKey: 'customerName',
                  } satisfies CmiViewAllConfig,
                },
              },
              {
                path: ':customerName',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'customer_at_risk',
                    entityParamKey: 'customerName',
                    backRoute: '/customer-performance',
                    title: 'Customers At Risk',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'product-performance',
        loadComponent: () =>
          import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/product-performance/product-performance').then(
                (m) => m.ProductPerformance
              ),
          },
          {
            path: 'price-not-keeping-up-with-cost',
            loadComponent: () =>
              import('./layout/route-outlet-wrapper').then((m) => m.RouteOutletWrapper),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/view-all/view-all').then((m) => m.ViewAllPage),
                data: {
                  cmiViewAllConfig: {
                    investigationType: 'price_vs_cost',
                    title: 'Price Not Keeping Up With Cost',
                    backRoute: '/product-performance',
                    parentRoute: '/product-performance',
                    drilldownRoute: '/product-performance/price-not-keeping-up-with-cost',
                    entityParamKey: 'product',
                  } satisfies CmiViewAllConfig,
                },
              },
              {
                path: ':product',
                loadComponent: () =>
                  import('./pages/insight-drilldown/insight-drilldown').then(
                    (m) => m.InsightDrilldownPage
                  ),
                data: {
                  cmiDrilldownConfig: {
                    investigationType: 'price_vs_cost',
                    entityParamKey: 'product',
                    backRoute: '/product-performance',
                    title: 'Price Not Keeping Up With Cost',
                  } satisfies CmiDrilldownConfig,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'agents/customer-risk',
        canActivate: [agentsNavEnabledGuard],
        loadComponent: () => import('./shared/components/chat/chat').then((m) => m.ChatComponent),
        data: { chatConfigKey: 'customer-risk' },
      },
      {
        path: 'agents/product-pricing',
        canActivate: [agentsNavEnabledGuard],
        loadComponent: () => import('./shared/components/chat/chat').then((m) => m.ChatComponent),
        data: { chatConfigKey: 'product-pricing' },
      },
      {
        path: 'agents/sales-rep-performance',
        canActivate: [agentsNavEnabledGuard],
        loadComponent: () => import('./shared/components/chat/chat').then((m) => m.ChatComponent),
        data: { chatConfigKey: 'sales-rep' },
      },
      {
        path: 'price-not-keeping-up-with-cost',
        redirectTo: 'product-performance/price-not-keeping-up-with-cost',
        pathMatch: 'full',
      },
      {
        path: 'customers-at-risk',
        redirectTo: 'customer-performance/customers-at-risk',
        pathMatch: 'full',
      },
      {
        path: 'pricing',
        canActivate: [pricingEnabledGuard],
        loadComponent: () => import('./pages/pricing/pricing').then((m) => m.Pricing),
      },
      {
        path: 'scorecard',
        canActivate: [scorecardEnabledGuard],
        loadComponent: () => import('./pages/scorecard/scorecard').then((m) => m.Scorecard),
        title: 'Scorecard',
      },
      {
        path: 'cmi/insights/:insightId',
        loadComponent: () =>
          import('./pages/insight-drilldown/insight-drilldown').then((m) => m.InsightDrilldownPage),
      },
      {
        path: 'cmi/config',
        canActivate: [configurationNavEnabledGuard],
        loadComponent: () => import('./pages/cmi-config/cmi-config').then((m) => m.CMIConfigPage),
      },
      {
        // GF-3897 Phase 1 admin surface. Permission-gated against the DB
        // ``ui_config.manage`` key (not Auth0). Tenant users and
        // super_users without the grant are bounced to `/home` by the
        // guard.
        path: 'admin/configs',
        canActivate: [uiConfigAdminGuard],
        title: 'Tenant UI configuration',
        loadComponent: () =>
          import('./pages/admin/configs/admin-configs').then((m) => m.AdminConfigs),
      },
      {
        // GF-3991 Phase 1 admin surface. Permission-gated against the DB
        // ``tenant_memberships.manage`` key. Lets super_users with the
        // grant manage which ``tenant_user`` accounts can access the
        // currently selected tenant.
        path: 'admin/memberships',
        canActivate: [tenantMembershipsAdminGuard],
        title: 'Tenant user access',
        loadComponent: () =>
          import('./pages/admin/memberships/admin-memberships').then((m) => m.AdminMemberships),
      },
    ],
  },
];

/**
 * Catalog of page-level "view all" aggregates derived from `routes`.
 *
 * Every route whose `data.cmiViewAllConfig.investigationType` is the
 * empty string is treated as a roll-up page (today: `/home/insights`).
 * Consumers (e.g. the chat page-context builder) read this constant
 * instead of hardcoding the list, so adding a new aggregate is a
 * matter of declaring its route here and nothing else.
 */
export const CMI_AGGREGATE_VIEWS = deriveCmiAggregateViews(routes);
